import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UserPlus, CheckSquare, MessageSquare, ArrowRight, PartyPopper, Calendar, AlertCircle } from "lucide-react";

interface PostJoinWelcomeProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamName: string;
  teamId: string;
  challengeId: string;
  challengeTitle: string;
  isNewTeam?: boolean;
  onInviteClick?: () => void;
  // Event registration props
  eventId?: string | null;
  eventName?: string;
  eventSlug?: string;
  isRegisteredForEvent?: boolean;
}

const PostJoinWelcome: React.FC<PostJoinWelcomeProps> = ({
  open,
  onOpenChange,
  teamName,
  teamId,
  challengeId,
  challengeTitle,
  isNewTeam = false,
  onInviteClick,
  eventId,
  eventName,
  eventSlug,
  isRegisteredForEvent = true,
}) => {
  const navigate = useNavigate();

  const handleGoToDashboard = () => {
    onOpenChange(false);
    navigate("/dashboard/challenges");
  };

  const handleViewTasks = () => {
    onOpenChange(false);
    // Navigate to challenges page and the modal will open there
    navigate(`/challenges?view=${challengeId}&tab=tasks`);
  };

  const handleViewUpdates = () => {
    onOpenChange(false);
    navigate(`/challenges?view=${challengeId}&tab=updates`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <PartyPopper className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-xl">
            {isNewTeam ? "Team Created!" : "Welcome to the Team!"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            {isNewTeam ? (
              <>You've created <strong>{teamName}</strong> for "{challengeTitle}"</>
            ) : (
              <>You've joined <strong>{teamName}</strong> for "{challengeTitle}"</>
            )}
          </p>
        </DialogHeader>

        {/* Event Registration CTA - shown if challenge has event and user isn't registered */}
        {eventId && !isRegisteredForEvent && eventSlug && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4 mb-2">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Don't forget to register for the event!
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  This challenge is part of <strong>{eventName}</strong>. You must register to participate in person.
                </p>
                <Button 
                  asChild 
                  size="sm" 
                  className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Link to={`/events/${eventSlug}`}>
                    <Calendar className="mr-2 h-4 w-4" />
                    Register for Event
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3 py-4">
          {onInviteClick && (
            <button
              onClick={() => {
                onOpenChange(false);
                onInviteClick();
              }}
              className="w-full flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-accent transition-colors text-left"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <UserPlus className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Invite Team Members</p>
                <p className="text-sm text-muted-foreground">Help grow your team by inviting others</p>
              </div>
            </button>
          )}

          <button
            onClick={handleViewTasks}
            className="w-full flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-accent transition-colors text-left"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-500/10">
              <CheckSquare className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="font-medium">View Tasks</p>
              <p className="text-sm text-muted-foreground">See what work needs to be done</p>
            </div>
          </button>

          <button
            onClick={handleViewUpdates}
            className="w-full flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-accent transition-colors text-left"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/10">
              <MessageSquare className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="font-medium">Team Updates</p>
              <p className="text-sm text-muted-foreground">Catch up on recent activity</p>
            </div>
          </button>
        </div>

        <Button onClick={handleGoToDashboard} className="w-full gap-2">
          Go to My Challenges
          <ArrowRight className="h-4 w-4" />
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default PostJoinWelcome;
